/**
 * Global UX enhancements applied once at app startup.
 *
 * Numeric-zero-clear-on-focus:
 *   <input type="number"> in Chromium/Safari doesn't expose selectionStart or
 *   accept .select(), so we can't "select all" a numeric field. Instead, when
 *   the user focuses a numeric input whose value is "0" (or "0.0"…), we blank
 *   the DOM value directly — without dispatching an input event. React reads
 *   the state (which is still 0) but only re-renders when state changes, so
 *   the field appears empty. As soon as the user types, React's onChange fires
 *   with the new (non-zero) value and the display syncs up normally.
 *   On blur, if the field is still empty (user tabbed away without typing),
 *   we restore the original "0" so the controlled state and the DOM stay in
 *   sync.
 */
export function installFormUX() {
  if (typeof document === "undefined") return;
  if (document.__zen_form_ux_installed) return;
  document.__zen_form_ux_installed = true;

  const isNumericInput = (el) => {
    if (!el || el.tagName !== "INPUT") return false;
    const type = (el.getAttribute("type") || "text").toLowerCase();
    const inputmode = (el.getAttribute("inputmode") || "").toLowerCase();
    return type === "number" || inputmode === "numeric" || inputmode === "decimal";
  };

  const isZeroString = (v) => v === "" ? false : /^0+(\.0+)?$/.test(String(v));

  document.addEventListener("focusin", (e) => {
    const el = e.target;
    if (!isNumericInput(el)) return;
    if (!isZeroString(el.value)) return;
    // Remember what we cleared and blank the DOM. Don't dispatch input event —
    // React's controlled state stays at 0, no re-render, field looks empty.
    el.dataset.zenZeroCleared = el.value;
    // eslint-disable-next-line no-param-reassign
    el.value = "";
  }, true);

  document.addEventListener("focusout", (e) => {
    const el = e.target;
    if (!isNumericInput(el)) return;
    const cleared = el.dataset.zenZeroCleared;
    delete el.dataset.zenZeroCleared;
    if (cleared === undefined) return;
    // Restore only if the user didn't type anything (field is still empty)
    // AND React's controlled value is still zero (defaultProps.value === "0"),
    // otherwise leave the user's typed value alone.
    if (el.value === "") {
      // eslint-disable-next-line no-param-reassign
      el.value = cleared;
    }
  }, true);
}
